import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Target, ArrowLeft, ArrowRight, Sparkles, Award } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { useDocumentMeta } from '../lib/useDocumentMeta';

interface Drill {
  id: string;
  drill_date: string;
  prompt: string;
  difficulty: 'easy' | 'medium' | 'hard';
  topic: string;
  tool: string;
}

interface Attempt {
  submission: string;
  score: number;
  feedback: string;
  xp_awarded: number;
  submitted_at: string;
}

interface EarnedAchievement {
  slug: string;
  title: string;
  description: string;
}

const DIFFICULTY: Record<string, string> = {
  easy: 'text-mint-700 bg-mint-50 border-mint-200',
  medium: 'text-sun-600 bg-sun-50 border-sun-200',
  hard: 'text-coral-600 bg-coral-50 border-coral-200',
};

export default function DrillPage() {
  useDocumentMeta({
    title: 'Daily Drill — PromptGPT',
    description: 'One daily prompt-engineering challenge to sharpen your AI skills.',
    canonical: 'https://promptgpt.ai/drills',
  });

  const { user, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState<Drill | null>(null);
  const [attempt, setAttempt] = useState<Attempt | null>(null);
  const [submission, setSubmission] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ score: number; feedback: string; xp_awarded: number } | null>(null);
  const [earnedAchievements, setEarnedAchievements] = useState<EarnedAchievement[]>([]);
  const [error, setError] = useState<string | null>(null);

  const drillUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/daily-drill`;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  const authHeaders = async (): Promise<Record<string, string>> => {
    const { data: { session } } = await supabase.auth.getSession();
    return {
      'Content-Type': 'application/json',
      'apikey': anonKey || '',
      'Authorization': `Bearer ${session?.access_token || anonKey || ''}`,
    };
  };

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const res = await fetch(drillUrl, { headers: await authHeaders() });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          setError(err.error || "Could not load today's drill");
          return;
        }
        const data = await res.json();
        setDrill(data.drill);
        if (data.attempt) setAttempt(data.attempt);
      } catch {
        setError("Could not load today's drill");
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const handleSubmit = async () => {
    if (!drill || submission.trim().length < 10) {
      toast.error('Write at least 10 characters');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(drillUrl, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ submission: submission.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error || 'Submission failed');
        return;
      }
      setResult({ score: data.score, feedback: data.feedback, xp_awarded: data.xp_awarded });
      refreshProfile();
      if (data.newly_earned?.length > 0) {
        const { data: achievements } = await supabase
          .from('achievements')
          .select('slug, title, description')
          .in('slug', data.newly_earned);
        if (achievements) {
          setEarnedAchievements(achievements as EarnedAchievement[]);
          (achievements as EarnedAchievement[]).forEach((a) => {
            toast.success(`Achievement unlocked: ${a.title}`, { duration: 5000 });
          });
        }
      }
    } catch {
      toast.error('Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container-app py-16 max-w-2xl">
        <div className="text-sm text-ink-500">Loading today's drill…</div>
      </div>
    );
  }

  if (error || !drill) {
    return (
      <div className="container-app py-16 max-w-2xl">
        <Link to="/learn" className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900 mb-6">
          <ArrowLeft className="w-4 h-4" /> Today
        </Link>
        <div className="bg-warm-100 border border-ink-200 rounded-2xl p-10 text-center">
          <Target className="w-8 h-8 text-ink-300 mx-auto mb-4" />
          <h2 className="font-display text-2xl mb-2">No drill today</h2>
          <p className="text-ink-600 text-sm mb-6">{error || 'Check back soon.'}</p>
          <Link to="/learn" className="btn-secondary">Back to Today</Link>
        </div>
      </div>
    );
  }

  const showResult = result !== null || attempt !== null;
  const displayScore = result?.score ?? attempt?.score ?? 0;
  const displayFeedback = result?.feedback ?? attempt?.feedback ?? '';
  const displayXp = result?.xp_awarded ?? attempt?.xp_awarded ?? 0;
  const isAlreadyDone = attempt !== null && result === null;

  const scoreColor =
    displayScore >= 90 ? 'text-mint-700' :
    displayScore >= 70 ? 'text-accent-700' :
    displayScore >= 50 ? 'text-sun-600' :
    'text-coral-600';

  const diffStyle = DIFFICULTY[drill.difficulty] || 'text-ink-500 bg-warm-100 border-ink-200';

  return (
    <div className="container-app py-10 max-w-2xl">
      <Link to="/learn" className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900 mb-6">
        <ArrowLeft className="w-4 h-4" /> Today
      </Link>

      <div className="mb-6">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500">
            Daily Drill · {new Date(drill.drill_date + 'T12:00:00').toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}
          </span>
          <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${diffStyle}`}>
            {drill.difficulty}
          </span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl leading-tight">
          {showResult ? 'Drill complete.' : "Today's challenge."}
        </h1>
      </div>

      <div className="bg-white border border-ink-100 rounded-2xl p-6 mb-6">
        {drill.topic && (
          <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-2">{drill.topic}</div>
        )}
        <p className="text-ink-800 leading-relaxed text-[17px]">{drill.prompt}</p>
      </div>

      {showResult ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-4"
        >
          <div className="bg-white border border-ink-100 rounded-2xl p-6">
            <div className="flex items-end justify-between mb-5">
              <div>
                <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-1">Score</div>
                <div className={`font-display text-7xl font-semibold leading-none ${scoreColor}`}>{displayScore}</div>
              </div>
              <div className="flex items-center gap-1.5 text-accent-700 font-bold text-lg pb-1">
                <Sparkles className="w-5 h-5" />
                +{displayXp} XP
              </div>
            </div>
            {displayFeedback && (
              <p className="text-ink-700 leading-relaxed text-sm border-t border-ink-100 pt-4">{displayFeedback}</p>
            )}
          </div>

          {isAlreadyDone && attempt.submission && (
            <div className="bg-white border border-ink-100 rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-2">Your submission</div>
              <p className="text-ink-700 leading-relaxed text-sm">{attempt.submission}</p>
            </div>
          )}

          {earnedAchievements.length > 0 && (
            <div className="bg-accent-50 border border-accent-200 rounded-2xl p-5">
              <div className="text-xs uppercase tracking-wider font-semibold text-accent-700 mb-3">
                Achievements unlocked
              </div>
              <div className="space-y-2.5">
                {earnedAchievements.map((a) => (
                  <div key={a.slug} className="flex items-start gap-3">
                    <Award className="w-4 h-4 text-accent-600 shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-semibold text-accent-900">{a.title}</div>
                      <div className="text-xs text-accent-700">{a.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isAlreadyDone && (
            <p className="text-center text-xs text-ink-500">
              Already completed today. Come back tomorrow for a new challenge.
            </p>
          )}

          <div className="flex items-center gap-3 pt-1">
            <Link to="/learn" className="btn-primary flex-1 justify-center">
              Back to Today
            </Link>
            <Link to="/skill-paths" className="btn-secondary flex-1 justify-center">
              Browse paths
            </Link>
          </div>
        </motion.div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-ink-500 font-semibold block mb-2">
              Write your prompt or response
            </label>
            <textarea
              value={submission}
              onChange={(e) => setSubmission(e.target.value)}
              placeholder="Write your prompt here — be specific, add context, and set clear constraints."
              rows={7}
              className="w-full border border-ink-200 rounded-xl px-4 py-3 text-ink-800 leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-transparent transition"
            />
            <div className="flex items-center justify-between mt-1.5">
              <p className="text-xs text-ink-400">One attempt per day · graded by AI</p>
              <span className={`text-xs font-mono ${submission.length >= 10 ? 'text-mint-700' : 'text-ink-400'}`}>
                {submission.length} / 10+
              </span>
            </div>
          </div>
          <button
            onClick={handleSubmit}
            disabled={submitting || submission.trim().length < 10}
            className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              'Grading…'
            ) : (
              <>Submit for grading <ArrowRight className="w-4 h-4" /></>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
